/*
 * @Author: Dong
 * @Date: 2022-04-06 09:35:47
 * @LastEditors: Please set LastEditors
 * @LastEditTime: 2023-07-11 14:34:58
 */
import React, { useState, forwardRef, useImperativeHandle, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { reducerStates } from 'root/store/types';
import { Link } from 'react-router-dom';
import { Layout, Menu, Popover, Form, Input, message, Modal } from 'sup-ui';
import { menuRoutes } from 'root/routers';
import classnames from 'classnames';
import Icon from 'root/components/Icon';
import logo from 'root/assets/images/logo_yunwei.svg';
import portrait from 'root/assets/images/portrait.png';

import styles from './SiderMenu.less';
import { useIntl } from 'react-intl';
import messages from './messages';
import { FormComponentProps } from 'sup-ui/lib/form';
import { logout, updatePassword } from 'root/services/user';
import md5 from 'js-md5';

const { Sider } = Layout;
const formItemLayout = {
  labelCol: {
    span: 4,
  },
  wrapperCol: {
    span: 20,
  },
};
const { confirm } = Modal;

const SiderMenu = forwardRef<FormComponentProps>((props: any, ref: any) => {
  const intl = useIntl();
  const { className } = props;
  const { form } = props;
  const account = localStorage.getItem('account');
  const { noSupos, hasSuposCatalogue } = useSelector((state: reducerStates) => state.common);
  const { initPassword } = useSelector((state: reducerStates) => state.envManage);
  const hasDirectory = hasSuposCatalogue || sessionStorage.getItem('hasSuposCatalogue');

  useImperativeHandle(ref, () => ({
    form,
  }));
  const { getFieldDecorator } = form;

  const classname = classnames({ [`${className}`]: !!className });
  const [visible, setVisible] = useState(false);
  const [editPasswordVisible, setEditPasswordVisible] = useState(false);

  // 操作菜单（退出登录/修改密码）显隐
  const handleVisibleChange = () => {
    setVisible(!visible);
  };

  // 如果是初始密码,强制要求修改密码
  useEffect(() => {
    if (initPassword) setEditPasswordVisible(true);
  }, [initPassword]);

  // 修改密码
  const editPassword = () => {
    form.validateFields((err: any, values: any) => {
      const { password, newPassword } = values;

      const params = {
        account,
        oldPassword: md5(password),
        newPassword: md5(newPassword),
      };
      if (!err) {
        updatePassword(params)
          .then(() => {
            message.success(intl.formatMessage(messages.success));
            toggleEditPasswordVisible();
          })
          .catch((err) => {
            if (['000061', '000062', '000064'].includes(err?.data?.code)) {
              form.setFields({
                password: { value: password, errors: [new Error(err?.data?.message)] },
              });
            }
          });
      }
    });
  };

  // 修改密码弹框显隐
  const toggleEditPasswordVisible = () => {
    setVisible(false);
    setEditPasswordVisible(!editPasswordVisible);
  };

  // 退出登录
  const logOut = () => {
    setVisible(false);
    confirm({
      title: intl.formatMessage(messages.confirmLogOut),
      onOk: () => {
        logout().then(() => {
          window.location.href = '/#/login';
        });
      },
    });
  };

  return (
    <Sider className={classname} trigger={null} collapsible>
      <img className={styles.logo} src={logo} alt="" />
      <Menu theme="dark" mode="inline" defaultSelectedKeys={[]}>
        {menuRoutes.length !== 0 &&
          menuRoutes[0].children?.length !== 0 &&
          (menuRoutes[0].children || []).map((router) => {
            const { path, title, icon } = router;
            if (!title) return false;
            if (router?.path && noSupos && ['monitor', 'syncBack'].includes(router.path)) {
              router.disabled = true;
            }
            return (
              <Menu.Item key={path} disabled={router.disabled || (router.path === 'packageManage' && !hasDirectory)}>
                <Link to={path || ''}>
                  {icon ? <Icon className={styles.icon} type={icon} /> : null}
                  <span className={styles.title}>{typeof title === 'object' ? intl.formatMessage(title) : title}</span>
                </Link>
              </Menu.Item>
            );
          })}
      </Menu>
      <div className={styles.userName}>
        <img src={portrait} width="25px" className={styles.portrait} />
        {account}
        <Popover
          overlayClassName={styles.customPopover}
          content={
            <ul className={styles.list}>
              <li onClick={toggleEditPasswordVisible}>{intl.formatMessage(messages.editPassword)}</li>
              <li onClick={logOut}>{intl.formatMessage(messages.logOut)}</li>
            </ul>
          }
          trigger="click"
          visible={visible}
          placement="topLeft"
          onVisibleChange={handleVisibleChange}
        >
          <span className={styles.ellipsis}>...</span>
        </Popover>
      </div>
      <Modal
        className={styles.editSysTimeBox}
        bodyStyle={{ maxHeight: 500, overflowY: 'auto', paddingBottom: 0 }}
        title={intl.formatMessage(messages.editPassword)}
        visible={editPasswordVisible}
        onOk={editPassword}
        height="auto"
        onCancel={toggleEditPasswordVisible}
        maskClosable={false}
        closable={!initPassword}
        cancelButtonProps={{ style: initPassword ? { display: 'none', background: 'red' } : {} }}
      >
        <Form {...formItemLayout}>
          <Form.Item label={intl.formatMessage(messages.curPassword)}>
            {getFieldDecorator('password', {
              rules: [
                {
                  required: true,
                  message: intl.formatMessage(messages.curPasswordNull),
                },
              ],
            })(<Input.Password maxLength="64" />)}
          </Form.Item>

          <Form.Item label={intl.formatMessage(messages.newPassword)}>
            {getFieldDecorator('newPassword', {
              rules: [
                {
                  required: true,
                  message: intl.formatMessage(messages.newPasswordNull),
                },
                {
                  validator: (rule: any, value: string, callbackFn: Function) => {
                    const reg =
                      /^(?![A-Za-z0-9]+$)(?![a-z0-9!@#$%^&*();?.,]+$)(?![A-Za-z!@#$%^&*();?.,]+$)(?![A-Z0-9!@#$%^&*();?.,]+$)[a-zA-Z0-9!@#$%^&*();?.,]{8,32}$/;

                    if (form.getFieldValue('password') === value) {
                      callbackFn(intl.formatMessage(messages.samePassword));
                    }
                    if (!reg.test(value)) {
                      callbackFn(intl.formatMessage(messages.newPasswordEdrror));
                    }
                    callbackFn();
                  },
                },
              ],
            })(<Input.Password maxLength="64" />)}
          </Form.Item>
          <Form.Item label={intl.formatMessage(messages.confirmPassword)}>
            {getFieldDecorator('confirmPassword', {
              rules: [
                {
                  required: true,
                  message: intl.formatMessage(messages.confirmPasswordNull),
                },
                {
                  validator: (rule: any, value: string, callbackFn: Function) => {
                    if (form.getFieldValue('newPassword') !== value) {
                      callbackFn(intl.formatMessage(messages.disaccord));
                    }
                    callbackFn();
                  },
                },
              ],
            })(<Input.Password maxLength="64" />)}
          </Form.Item>
        </Form>
      </Modal>
    </Sider>
  );
});

export default Form.create()(SiderMenu);
